/**
 * The six status values the Connectivity service serialises to the dashboard.
 *
 * The service tracks finer-grained internal states (terraform TF_PLANNING /
 * TF_APPLYING, checker REJECTED / EXPIRED) but collapses them before
 * responding: the terraform steps read as PENDING, REJECTED and EXPIRED read as
 * FAILED. Nothing outside this enum ever reaches the UI.
 */
export enum VPCPeeringStatusValue {
  PENDING = 'PENDING',
  REQUEST_SUBMITTED = 'REQUEST_SUBMITTED',
  PENDING_ACCEPTANCE = 'PENDING_ACCEPTANCE',
  ACTIVE = 'ACTIVE',
  FAILED = 'FAILED',
  DELETED = 'DELETED'
}

/** Row actions the server grants per record. Never inferred from status. */
export enum VPCPeeringAction {
  EDIT = 'edit',
  RESUBMIT = 'resubmit',
  DELETE = 'delete'
}

/**
 * How a submitted `cidr` list is applied. `ADDITIVE_ONLY` (a live peering) means
 * the list must still contain every current entry — an omission is a removal,
 * which deletes a live route and is refused with a 400.
 */
export enum CIDREditMode {
  REPLACE = 'replace',
  ADDITIVE_ONLY = 'additive_only'
}

/** Which form the peering modal is rendering. */
export enum VPCPeeringFormMode {
  CREATE = 'create',
  EDIT = 'edit',
  RESUBMIT = 'resubmit'
}

/**
 * Fields the API names in `editable_fields`. These are wire names, so `cidr` —
 * not the `cidr_range` the form binds to.
 */
export enum VPCPeeringEditableField {
  NAME = 'name',
  CIDR = 'cidr',
  AWS_REGION = 'aws_region'
}

export interface VPCPeeringStatus {
  name: string;
  value: VPCPeeringStatusValue;
  colorType?: string;
  description?: string;
}

export interface VPCPeeringConnection {
  id: string;
  name: string | null;
  status: VPCPeeringStatus;
  aws_account_id: string;
  vpc_id: string;
  cidr: string[];
  aws_region: string;
  hevo_region: string;
  team_name: string;
  team_id: number;
  peering_id: string | null;
  hevo_cidr: string | null;
  hevo_tunnel_private_ip: string | null;
  hevo_tunnel_public_ip: string | null;
  message: string | null;
  // Machine-readable cause of a failure and one sentence of customer-safe
  // guidance. Null unless `status` is FAILED. Prefer `failure_hint` over
  // `message` for anything shown to a customer.
  failure_code: string | null;
  failure_hint: string | null;
  // Server-owned UI policy. The dashboard renders what these say rather than
  // deriving rules from `status`, so a policy change lands on the next poll
  // with no frontend release.
  actions: VPCPeeringAction[];
  editable_fields: VPCPeeringEditableField[];
  cidr_edit_mode: CIDREditMode | null;
  // True while a background job is working on this record. The status stays
  // truthful throughout (an ACTIVE peering really is serving traffic during a
  // route-table update), so this is what explains a temporarily missing action.
  change_in_progress: boolean;
  created_at: number;
  deleted_at: number | null;
}

export interface CreateVPCConnectionPayload {
  name: string;
  aws_account_id: string;
  vpc_id: string;
  cidr: string[];
  aws_region: string;
  hevo_region: string;
  team_name: string;
  team_id: number;
}

/**
 * Body for both PATCH and resubmit — the service takes the same shape for each,
 * so "Fix & Retry" is one call rather than a save that might never be retried.
 * `cidr` is always the full desired list, never a delta.
 */
export type EditVPCConnectionPayload = Partial<
  Pick<CreateVPCConnectionPayload, 'name' | 'cidr' | 'aws_region'>
>;

export const hasAction = (
  connection: VPCPeeringConnection,
  action: VPCPeeringAction
): boolean => (connection.actions ?? []).includes(action);
